/**
 * Widget Renderer Component
 *
 * Maps widget IDs to their respective components and renders them.
 * Uses React.lazy for new widgets to enable code-splitting.
 * Provides fallback for unimplemented widgets.
 *
 * @author BizClick WMS
 * @version 4.0.0
 */

import React, { Suspense } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { WidgetInstance } from '../types';
import { getWidgetById } from '../widgetRegistry';

// Import existing widget components (eager - already loaded)
import {
  ShippingStatsWidget,
  ActivityFeedWidget,
  OperatorStatusWidget,
} from './widgets';

// Lazy-load new widgets for code splitting
const SparklineKPIWidget = React.lazy(() => import('./widgets/SparklineKPIWidget'));
const AnimatedCounterWidget = React.lazy(() => import('./widgets/AnimatedCounterWidget'));
const LeaderboardWidget = React.lazy(() => import('./widgets/LeaderboardWidget'));
const IncomingDeliveriesWidget = React.lazy(() => import('./widgets/IncomingDeliveriesWidget'));
const BottleneckDetectorWidget = React.lazy(() => import('./widgets/BottleneckDetectorWidget'));
const ComparisonWidget = React.lazy(() => import('./widgets/ComparisonWidget'));
const OrderFlowWidget = React.lazy(() => import('./widgets/OrderFlowWidget'));
const MiniHeatmapWidget = React.lazy(() => import('./widgets/MiniHeatmapWidget'));
const ZoneUtilizationWidget = React.lazy(() => import('./widgets/ZoneUtilizationWidget'));
const RemainingOrdersWidget = React.lazy(() => import('./widgets/RemainingOrdersWidget'));
const CompassReportWidget = React.lazy(() => import('./widgets/CompassReportWidget'));

interface WidgetRendererProps {
  instance: WidgetInstance;
}

/**
 * Map of widget IDs to their components
 */
const WIDGET_COMPONENTS: Record<string, React.FC<{ instance: WidgetInstance }>> = {
  // === COMPASS ===
  'compass-report': CompassReportWidget,

  // === ORDERS ===
  'order-flow': OrderFlowWidget,

  // === PICKING ===
  'remaining-orders': RemainingOrdersWidget,

  // === RECEIVING ===
  'incoming-deliveries': IncomingDeliveriesWidget,

  // === INVENTORY ===
  'zone-utilization': ZoneUtilizationWidget,

  // === SHIPPING ===
  'shipping-stats': ShippingStatsWidget,

  // === KPI ===
  'sparkline-kpi': SparklineKPIWidget,
  'animated-counter': AnimatedCounterWidget,

  // === ACTIVITY ===
  'activity-feed': ActivityFeedWidget,
  'mini-heatmap': MiniHeatmapWidget,

  // === OPERATORS ===
  'operator-status': OperatorStatusWidget,

  // === GAMIFICATION ===
  'leaderboard': LeaderboardWidget,

  // === ALERTS ===
  'bottleneck-detector': BottleneckDetectorWidget,

  // === COMPARISON ===
  'comparison': ComparisonWidget,
};

/**
 * Loading fallback for lazy-loaded widgets
 */
const WidgetLoadingFallback: React.FC = () => (
  <div className="h-full w-full bg-gray-800 rounded-lg border-2 border-gray-700 flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
  </div>
);

/**
 * Fallback component for unimplemented widgets
 */
const FallbackWidget: React.FC<{ instance: WidgetInstance }> = ({ instance }) => {
  const widgetDef = getWidgetById(instance.widgetId);

  return (
    <div className="h-full w-full bg-gray-800 rounded-lg border-2 border-gray-700 flex flex-col items-center justify-center p-4">
      <AlertCircle className="w-8 h-8 text-gray-500 mb-2" />
      <p className="text-sm text-gray-400 text-center">
        Widget ikke implementert
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {widgetDef?.name || instance.widgetId}
      </p>
    </div>
  );
};

/**
 * Widget Renderer Component
 */
const WidgetRenderer: React.FC<WidgetRendererProps> = ({ instance }) => {
  const WidgetComponent = WIDGET_COMPONENTS[instance.widgetId] || FallbackWidget;

  return (
    <Suspense fallback={<WidgetLoadingFallback />}>
      <WidgetComponent instance={instance} />
    </Suspense>
  );
};

export default WidgetRenderer;
